// ** React Imports
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Collapse,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Chip,
  MenuItem,
  Popover,
   ListSubheader ,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

// ** Icon Imports
import Icon from "components/icon";
import CostIcon from "../../../assets/WorkRequirements/CostIcon.png";
import PriceIcon from "../../../assets/WorkRequirements/PriceIcon.png";
import ProfitIcon from "../../../assets/WorkRequirements/ProfitIcon.png";
import ManhoursIcon from "../../../assets/WorkRequirements/MahoursIcon.png";
import WRIcon from "images/WR_Badge.png";
import CoinIcon from "images/CoinIcon.svg";
// ** Custom Component Imports
import CustomChip from "components/mui/chip";
import CustomTextField from "components/mui/text-field";

import AddPropertyAreaDialog from "screens/Customers/component/AddPropertyArea"; // adjust path
import AddIcon from "@mui/icons-material/Add";

// ** Component Imports
import LineItems from "./LineItems";
import JobDetailDialog from "screens/WorkOrders/component/JobDetail";
import JobScheduleDialog from "screens/WorkOrders/component/JobSchedule";
import { renderFilePreview } from "components/FilePrivews";
import dayjs from "dayjs";
import { getWorkRequirement } from "screens/WorkOrders/logic/WorkRequirementScheduleLogic";
import { formatUSCurrency } from "components/CurrencyFormat";
import { useLineItemLogic } from "../logic/line-item-logic";
import { GetBudgetedSummary } from "../logic/GetBudgettedSummary";
import { GetServiceRequestRaw } from "screens/Requests/logic/GetServiceRequest";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    placement="top-end"
    arrow
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#ddeefe",
    border: "1px solid #E1E4EA",
    whiteSpace: "nowrap",
    maxWidth: "unset",
    minWidth: "auto",
    padding: "8px 12px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#ddeefe",
  },
}));

// ==================

function OccurrencesInput({ workReq, rules, onChange }) {
  const isRecurring = workReq.jobType === "Recurring";

  // 1️⃣ Initialize to existing value or 1 if null/undefined
  const [local, setLocal] = useState(
    workReq.numberOfOccurrences != null ? workReq.numberOfOccurrences : 1
  );

  // 2️⃣ Sync whenever jobType or backend value changes.
  useEffect(() => {
    const defaultVal =
      workReq.numberOfOccurrences != null ? workReq.numberOfOccurrences : 1;
    setLocal(defaultVal);
  }, [workReq.jobType, workReq.numberOfOccurrences]);

  // 3️⃣ On blur, enforce minimum of 1 and push to parent
  const handleBlur = () => {
    let val = parseInt(local, 10);
    if (isNaN(val) || val < 1) val = 1;
    onChange(workReq.workReqID, "numberOfOccurrences", val);
    setLocal(val);
  };

  return (
    <Grid item xs={12} md={12} sx={{ mt: 2 }}>
      <FormLabel
        sx={{
          fontSize: 13,
          fontWeight: 400,
          mb: 1,
        }}
      >
        Number of Occurrences
      </FormLabel>
      <CustomTextField
        type="number"
        fullWidth
        disabled={!rules.canEdit || !isRecurring}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={handleBlur}
        inputProps={{ min: 1, step: 1 }}
        error={isRecurring && Number(local) < 1}
        helperText={
          isRecurring && Number(local) < 1 ? "Required for Recurring job" : ""
        }
        sx={{ backgroundColor: "#fff" }}
      />
    </Grid>
  );
}

function ServiceLineItems({
  entity,
  rules,
  setLoading,
  toast,
  workReq,
  setLineItemChanged,
}) {
  const templateJob = Array.isArray(workReq.jobs)
    ? workReq.jobs.find((j) => j.occurrenceNo === 0)
    : null;

  const budgetedLogic = useLineItemLogic({
    entity: "jobDetail",
    workReq,
    setLoading,
    toast,
    job: templateJob || {}, // Service has no "job", so pass a dummy
  });
  // console.log(workReq)

  const {
    LineItemProps,
    LineItemObservationProps,
    LineItemMaterialProps,
    LineItemLaborProps,
    LineItemSubcontractorProps,
    LineItemMiscCostProps,
  } = budgetedLogic;

  if (!templateJob) {
    // either show a loader, or nothing, or a “no data” message
    return <Typography>Loading line‐items…</Typography>;
  }

  return (
    <LineItems
      entity={entity}
      rules={rules}
      workReq={workReq}
      setLoading={setLoading}
      toast={toast}
      job={templateJob}
      LineItemProps={LineItemProps}
      LineItemObservationProps={LineItemObservationProps}
      LineItemMaterialProps={LineItemMaterialProps}
      LineItemLaborProps={LineItemLaborProps}
      LineItemSubcontractorProps={LineItemSubcontractorProps}
      LineItemMiscCostProps={LineItemMiscCostProps}
      setLineItemChanged={setLineItemChanged}
    />
  );
}
// ==================

const Service = ({
  entity,
  isChild,
  workRequirementList,
  openService,
  handleWorkRequirementToggle,
  handleAddInstruction,
  handleRemoveInstruction,
  handleToggleAlert,
  newInstruction,
  setNewInstruction,
  handleWorkRequirementChange,
  handleChange,
  PropertyAreas,
  fetchPropertyAreas,
  handleOpenWorkRequirementUploadDlg,
  showConfirmationAttachmentDelete,
  handleDownloadAttachment,
  attachmentMap,
  method,
  showConfirmationWorkRequirementDelete,
  rules,
  setLoading,
  toast,
  bundle,
  fetchWorkRequirements,
  requestID,
  fetchRequests,
  lineItemChanged,
  setLineItemChanged,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [openJobDetail, setOpenJobDetail] = useState(false);
  const [scheduleMap, setScheduleMap] = useState({});
  const [openSingleSchedule, setOpenSingleSchedule] = useState(false);
  const [serviceToSchedule, setServiceToSchedule] = useState(null);
  const [selectedWorkReq, setSelectedWorkReq] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [workReqIDs, setWorkReqIDs] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [jobsite, setJobsite] = useState({ jobsiteID: 0 });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const a = await GetServiceRequestRaw(0, requestID); // you already have this prop
        // console.log("a===")
        // console.log(a)
        const id = a?.result?.[0]?.jobSiteID ?? 0;
        if (mounted) setJobsite({ jobsiteID: id });
      } catch (e) {
        // swallow; leave jobsiteID = 0
        console.log(e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const summaryKey = useMemo(() => {
    if (!Array.isArray(workRequirementList)) return "";
    // include ID so order changes don't cause accidental merges
    return workRequirementList
      .map(
        (w) => `${w.workReqID}:${w.jobType ?? ""}:${w.numberOfOccurrences ?? 0}`
      )
      .join("|");
  }, [workRequirementList]);

  useEffect(() => {
    const fetchAllSummaries = async () => {
      console.log(summaryKey);
      const newSummaries = {};
      for (const workReq of workRequirementList || []) {
        const result = await GetBudgetedSummary(
          0,
          0,
          workReq.workReqID,
          navigate
        );
        newSummaries[workReq.workReqID] = Array.isArray(result)
          ? result
          : [result];
      }
      setSummaries(newSummaries);
    };

    if (workRequirementList?.length) {
      fetchAllSummaries();
    }
    // run when jobType/occurrences signature changes, or line items changed
  }, [summaryKey, lineItemChanged, workRequirementList, navigate]);

  // useEffect(() => {
  //   const fetchAllSummaries = async () => {
  //     const newSummaries = {};
  //     for (const workReq of workRequirementList) {
  //       const result = await GetBudgetedSummary(0, 0, workReq.workReqID, navigate);
  //       newSummaries[workReq.workReqID] = Array.isArray(result) ? result : [result];
  //     }
  //     // console.log(newSummaries)
  //     setSummaries(newSummaries);
  //   };

  //   if (workRequirementList?.length) {
  //     fetchAllSummaries();
  //   }
  // }, [workRequirementList, lineItemChanged]);

  const openScheduleFor = (wr) => {
    setServiceToSchedule(wr);
    setOpenSingleSchedule(true);
  };
  const closeSingleSchedule = () => {
    setOpenSingleSchedule(false);
    setServiceToSchedule(null);
  };

  const loadSchedule = async (workReqID, entityID) => {
    const wr = await getWorkRequirement({ workReqID, entityID });
    if (wr) setScheduleMap((m) => ({ ...m, [workReqID]: wr }));
  };

  useEffect(() => {
    Object.entries(openService).forEach(([key, isOpen]) => {
      if (!isOpen) return;
      const [, id] = key.split("-");
      const workReqID = Number(id);
      // force a reload even if scheduleMap[workReqID] already exists
      const wr = workRequirementList.find((w) => w.workReqID === workReqID);
      if (wr) loadSchedule(workReqID, wr.entityID);
    });
  }, [openService, workRequirementList]);

  const handleNewInstructionChange = (value, index) => {
    setNewInstruction((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const handleOpenJobDetail = (workReq, job) => {
    setSelectedWorkReq(workReq);
    setSelectedJob(job);
    setOpenJobDetail(true);
  };
  const handleCloseJobDetail = () => {
    setOpenJobDetail(false);
    setSelectedWorkReq(null);
    setSelectedJob(null);
  };

  const safeParseFirstAreaID = (workRequirementList, workReqID) => {
    try {
      const areaIDs = workRequirementList.find(
        (w) => w.workReqID === workReqID
      )?.propertyAreaIDs;
      return Array.isArray(areaIDs)
        ? areaIDs
        : typeof areaIDs === "string"
        ? JSON.parse(areaIDs)
        : [0];
    } catch {
      return [0];
    }
  };

  const serviceStyles = {
    border: "1px solid #E4E4E7",
    backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff",
    p: { xs: 0, sm: 4 },
  };

  // ** Property Area dialog states
  const [openPropertyAreaDlg, setOpenPropertyAreaDlg] = useState(false);
  const [creatingForWorkReqID, setCreatingForWorkReqID] = useState(null);

  const [propertyArea, setPropertyArea] = useState({
    propertyAreaID: 0,
    jobsiteID: 0,
    propertyAreaType: "Indoor",
    propertyAreaName: "",
    description: "",
    measurement: [],
  });
  const [areaMeasurement, setAreaMeasurement] = useState({
    measurementName: "",
    unitOfMeasure: "",
    length: "",
    width: "",
    height: "",
    total: "",
  });
  const [listOfMeasurements, setListOfMeasurements] = useState([]);

  const handleOpenPropertyAreaDlg = (workReqID) => {
    setCreatingForWorkReqID(workReqID);
    setPropertyArea({
      propertyAreaID: 0,
      jobsiteID: jobsite?.jobsiteID ?? 0,
      propertyAreaType: "Indoor",
      propertyAreaName: "",
      description: "",
      measurement: [],
    });
    setAreaMeasurement({
      measurementName: "",
      unitOfMeasure: "",
      length: "",
      width: "",
      height: "",
      total: "",
    });
    setListOfMeasurements([]);
    setOpenPropertyAreaDlg(true);
  };

  const handleClosePropertyAreaDlg = () => setOpenPropertyAreaDlg(false);

  // when the dialog reports a successful save, refresh areas and auto-select the new one
  const handlePropertyAreaSaved = async (newArea) => {
    try {
      // refresh list in parent
      if (jobsite?.jobsiteID) {
        await fetchPropertyAreas(jobsite.jobsiteID);
      }
      // auto-select the newly created area on the row where user clicked
      if (creatingForWorkReqID != null && newArea?.propertyAreaID) {
        const current = safeParseFirstAreaID(
          workRequirementList,
          creatingForWorkReqID
        );
        // if “Entire Property” (0) is selected, replace with only the new area
        const next = current.includes(0)
          ? [newArea.propertyAreaID]
          : [...new Set([...current, newArea.propertyAreaID])];

        // update UI state immediately
        handleChange(creatingForWorkReqID, "propertyAreaIDs", next);
        // trigger your persisted save (your hook listens on this)
        handleWorkRequirementChange(
          creatingForWorkReqID,
          "propertyAreaIDs",
          next
        );
      }
    } finally {
      setCreatingForWorkReqID(null);
    }
  };

  return (
    <>
      <List
        component="nav"
        aria-label="main mailbox"
        sx={isChild ? serviceStyles : {}}
      >
        {workRequirementList && workRequirementList.length > 0 ? (
          workRequirementList.map((workReq, index) => {
            const key = `${workReq.workReqID}`;
            // const jobs =
            //   scheduleMap[workReq.workReqID]?.jobs ?? workReq.jobs ?? [];
            const jobs = workReq.jobs || [];

            // include both scheduled and unscheduled jobs (occurrenceNo > 0)
            const validJobs = jobs.filter(
              (j) => j.occurrenceNo > 0
            );
            
            // separate scheduled and unscheduled jobs for different processing
            // const scheduledJobs = validJobs.filter((j) => j.scheduleStartDate && j.scheduleEndDate);
            const scheduledJobs = validJobs.filter((j) => j.scheduleStartDate || j.scheduleEndDate);

            let fixedRate = 0; 
            if(workReq?.billingType === 'Fixed'){
              if(workReq?.jobs && (workReq?.jobs || []).length > 0){
                fixedRate = workReq?.jobs?.find((wrj) => wrj.occurrenceNo > 0)?.flatRate ?? 0;
              }
            }

            let minStart = null;
            let maxEnd = null;
            if (scheduledJobs.length) {
              // Filter jobs that have start dates for minStart calculation
              const jobsWithStart = scheduledJobs.filter(j => j.scheduleStartDate);
              if (jobsWithStart.length) {
                minStart = jobsWithStart.reduce((earliest, j) => {
                  let d = dayjs(j.scheduleStartDate);
                  if (j.scheduleStartTime) {
                    const time = dayjs(`1970-01-01T${j.scheduleStartTime}`);
                    d = d.hour(time.hour()).minute(time.minute());
                  }
                  return earliest === null || d.isBefore(earliest) ? d : earliest;
                }, null);
              }
              
              // Filter jobs that have end dates for maxEnd calculation
              const jobsWithEnd = scheduledJobs.filter(j => j.scheduleEndDate);
              if (jobsWithEnd.length) {
                maxEnd = jobsWithEnd.reduce((latest, j) => {
                  let d = dayjs(j.scheduleEndDate);
                  if (j.scheduleEndTime) {
                    const time = dayjs(`1970-01-01T${j.scheduleEndTime}`);
                    d = d.hour(time.hour()).minute(time.minute());
                  }
                  return latest === null || d.isAfter(latest) ? d : latest;
                }, null);
              }
            }

            const summary = summaries[workReq.workReqID] || [];

            return (
              <React.Fragment key={key}>
                <ListItem
                  disablePadding
                  sx={{
                    borderBottom: "1px solid #E1E4EA",
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                  }}
                  key={index}
                >
                  <ListItemButton
                    onClick={() =>
                      handleWorkRequirementToggle(
                        index + "-" + workReq.workReqID,
                        openService[index + "-" + workReq.workReqID]
                      )
                    }
                    sx={{ width: "100%" }}
                  >
                    <Icon
                      icon={
                        openService[index + "-" + workReq.workReqID]
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: { xs: 0, md: 4 } }}
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: { xs: "flex-start", md: "center" },
                            gap: { xs: 1, md: 4 },
                          }}
                        >
                          <Typography variant="body1">
                            {workReq?.industryName} - {workReq?.subServiceName}
                          </Typography>
                          <CustomChip
                            skin="dark"
                            sx={{
                              height: 20,
                              minWidth: 60,
                              padding: "0 6px",
                              fontSize: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              borderRadius: "4px",
                              backgroundColor: "#1976d2", // Dark green or blue
                              color: "#fff",
                            }}
                            label={
                              <Typography
                                variant="caption"
                                color="white"
                                sx={{
                                  fontSize: "0.65rem",
                                  lineHeight: 1,
                                  textAlign: "center",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                SERVICE
                              </Typography>
                            }
                            size="small"
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1, md: 1 },
                    }}
                  >
                    {(entity === "estimate" ||
                      entity === "estimate-detail" ||
                      entity === "workOrder" ||
                      entity === "workOrder-detail" ||
                      entity === "workOrder-schedule") && (
                      <>
                        {(entity === "workOrder-detail" ||
                          entity === "workOrder-schedule") && (
                          <Box
                            sx={{
                              border: `1px solid ${theme.palette.divider}`,
                              px: 2,
                              py: 1,
                              ml: { xs: 2, md: 0 },
                              mb: { xs: 2, md: 0 },
                              display: "flex",
                              alignItems: "center",
                              whiteSpace: {xs:"wrap", sm:"nowrap"},
                              borderRadius: 1,
                              cursor: "pointer",
                            }}
                            // onClick={() => openScheduleFor(workReq)}
                            onClick={
                              rules.canEdit
                                ? () => openScheduleFor(workReq)
                                : undefined
                            }
                          >
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="text.primary"
                            >
                              From:{" "}
                              {minStart
                                ? (() => {
                                    // Format date, include time only if it exists (not 00:00:00)
                                    let dateStr = minStart.format("MM/DD/YY");
                                    if (minStart.hour() !== 0 || minStart.minute() !== 0) {
                                      dateStr += ` | ${minStart.format("h:mm A")}`;
                                    }
                                    return dateStr;
                                  })()
                                : "--"}{" "}
                              | Until:{" "}
                              {maxEnd
                                ? (() => {
                                    // Format date, include time only if it exists (not 00:00:00)
                                    let dateStr = maxEnd.format("MM/DD/YY");
                                    if (maxEnd.hour() !== 0 || maxEnd.minute() !== 0) {
                                      dateStr += ` | ${maxEnd.format("h:mm A")}`;
                                    }
                                    return dateStr;
                                  })()
                                : "--"}
                            </Typography>
                          </Box>
                        )}
                        <HtmlTooltip
                          title={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                color: "#FFCA28",
                              }}
                            >
                              <CustomChip
                                skin="light"
                                color="info"
                                icon={<img src={CostIcon} />}
                                label={
                                  <>
                                    <Typography
                                      variant="body2"
                                      color="info.main"
                                    >
                                      Cost{" "}
                                      <strong>
                                        {formatUSCurrency(
                                          summary[0]?.totalCost || 0
                                        )}
                                      </strong>
                                    </Typography>
                                  </>
                                }
                              />
                              <CustomChip
                                skin="light"
                                color="info"
                                icon={<img src={PriceIcon} />}
                                label={
                                  <>
                                    <Typography
                                      variant="body2"
                                      color="info.main"
                                    >
                                      Price{" "}
                                      <strong>
                                        {formatUSCurrency(
                                          workReq?.billingType === 'Fixed' ? fixedRate : (summary[0]?.totalPrice || 0)
                                        )}
                                      </strong>
                                    </Typography>
                                  </>
                                }
                              />
                              <CustomChip
                                skin="light"
                                color="info"
                                icon={<img src={ManhoursIcon} />}
                                label={
                                  <>
                                    <Typography
                                      variant="body2"
                                      color="info.main"
                                    >
                                      Manhours{" "}
                                      <strong>
                                        {summary[0]?.totalManHours || 0}h
                                      </strong>
                                    </Typography>
                                  </>
                                }
                              />
                              <CustomChip
                                skin="light"
                                color="info"
                                icon={<img src={ProfitIcon} />}
                                label={
                                  <>
                                    <Typography
                                      variant="body2"
                                      color="info.main"
                                    >
                                      Profit{" "}
                                      <strong>
                                        {formatUSCurrency(
                                          summary[0]?.totalProfit || 0
                                        )}
                                      </strong>
                                    </Typography>
                                  </>
                                }
                              />
                            </Box>
                          }
                        >
                          <Box
                            sx={{
                              p: "0px !important",
                              backgroundColor: "#EEF2FF",
                              borderRadius: 2,
                              cursor: "pointer",
                              display: "inline-flex", // Ensures proper alignment
                              "&:hover": {
                                backgroundColor: "#E0E7FF", // Optional hover effect
                              },
                            }}
                          >
                            <img src={CoinIcon} width="35" height="35" />
                          </Box>
                        </HtmlTooltip>
                      </>
                    )}
                    <IconButton
                      color="error"
                      onClick={() => {
                        showConfirmationWorkRequirementDelete(
                          workReq.workReqID,
                          bundle
                        );
                      }}
                    >
                      <Icon icon="tabler:trash" />
                    </IconButton>
                  </Box>
                </ListItem>
                <Collapse
                  in={openService[index + "-" + workReq.workReqID]}
                  timeout="auto"
                  unmountOnExit
                  sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#F6F7FA" }}
                >
                  <Grid container spacing={4} p={4}>
                    <Grid item xs={12} md={7}>
                      <FormLabel
                        sx={{
                          fontSize: "13px",
                          color: "text.primary",
                          fontWeight: 400,
                          marginBottom: "4px",
                          color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                          "& .MuiFormLabel-asterisk": {
                            color: "error.main",
                          },
                        }}
                      >
                        Description
                      </FormLabel>
                      <Box>
          <CustomTextField
            fullWidth
            rows={4}
            maxRows={8}
            multiline
            inputProps={{
              maxLength: 2000
            }}
            value={
              workRequirementList.find(
                (w) => w.workReqID === workReq.workReqID
              )?.description
            }
            onChange={(e) => {
              handleChange(
                workReq.workReqID,
                "description",
                e.target.value
              );
            }}
            onBlur={(e) => {
              handleWorkRequirementChange(
                workReq.workReqID,
                "description",
                e.target.value
              );
            }}
            placeholder="Add a description ..."
            sx={{
              "& .MuiInputBase-root": {
                border: "1px solid #D9D9D9",
                boxShadow: "none",
                backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff !important",
                maxHeight: "200px",
                overflowY: "auto"
              },
            }}
            disabled={!rules.canEdit} // Disable if not editable
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <FormLabel
                        sx={{
                          fontSize: "13px",
                          color: "text.primary",
                          fontWeight: 400,
                          mb: "4px",
                        }}
                      >
                        Attachments ({" "}
                        {attachmentMap?.[workReq.workReqID]?.length || 0} )
                      </FormLabel>
                      <Box
                        sx={{
                          display: "flex",
                          mt: 2,
                          gap: 5,
                          overflowX: "auto",
                          padding: 2,
                        }}
                      >
                        {attachmentMap &&
                          // attachmentMap[workReq.workReqID] &&
                          // attachmentMap[workReq.workReqID].map(
                          //   (file, index) => (                            
                            Array.isArray(attachmentMap?.[workReq.workReqID]) &&
                            attachmentMap[workReq.workReqID].map((file, index) => (
                              <ListItem
                                sx={{
                                  display: "flex",
                                  gap: 2,
                                  width: "max-content",
                                  boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                                  borderRadius: "10px",
                                }}
                                key={index}
                              >
                                <Tooltip title={file.fileName}>
                                  <Box>{renderFilePreview(file.fileName)}</Box>
                                </Tooltip>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Tooltip title="Download">
                                    <IconButton
                                    color="primary"
                                      onClick={() =>
                                        handleDownloadAttachment(
                                          file.fileName,
                                          workReq.workReqID,
                                          "Request"
                                        )
                                      }
                                    >
                                      <Icon
                                        icon="tabler:download"
                                        fontSize={20}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remove">
                                    <IconButton
                                      color="error"
                                      onClick={() =>
                                        showConfirmationAttachmentDelete(
                                          file.mediaID,
                                          workReq.workReqID
                                        )
                                      }
                                      disabled={!rules.canEdit}
                                    >
                                      <Icon icon="tabler:trash" fontSize={20} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </ListItem>
                            )
                          )}
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => {
                            handleOpenWorkRequirementUploadDlg(
                              workReq.workReqID
                            );
                          }}
                          disabled={!rules.canEdit}
                        >
                          <Icon icon="tabler:plus" sx={{ fontSize: 20 }} />
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <FormLabel
                        sx={{
                          fontSize: "13px",
                          color: "text.primary",
                          fontWeight: 400,
                          mb: "4px",
                        }}
                      >
                        Special Instructions (if any)
                      </FormLabel>
                      {workRequirementList &&
                        workRequirementList
                          .find((w) => w.workReqID === workReq.workReqID)
                          ?.specialInstructions.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                border: "1px solid #D0D5DD",
                                borderRadius: 1,
                                gap: 2,
                                px: 2,
                                mb: 2,
                              }}
                            >
                              <Box>
                                <Typography variant="body2">{`${index + 1}. ${
                                  item.instructionText
                                }`}</Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <FormControlLabel
                                  sx={{ whiteSpace: "nowrap" }}
                                  control={
                                    <Checkbox
                                      checked={item.isAlert}
                                      onChange={() =>
                                        handleToggleAlert(
                                          workReq.workReqID,
                                          index
                                        )
                                      }
                                      color="success"
                                      disabled={!rules.canEdit}
                                    />
                                  }
                                  label="Alert On"
                                />
                                <IconButton
                                  onClick={() =>
                                    handleRemoveInstruction(
                                      workReq.workReqID,
                                      index
                                    )
                                  }
                                  disabled={!rules.canEdit}
                                >
                                  <Icon icon="tabler:x" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <CustomTextField
                          fullWidth
                          sx={{
                            "& .MuiInputBase-root": {
                              border: "1px solid #D9D9D9",
                              boxShadow: "none",
                              backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff !important",
                            },
                          }}
                          placeholder="Add a special instruction"
                          value={
                            newInstruction[index + "-" + workReq.workReqID] ||
                            ""
                          }
                          onChange={(e) =>
                            handleNewInstructionChange(
                              e.target.value,
                              index + "-" + workReq.workReqID
                            )
                          }
                          disabled={!rules.canEdit}
                        />
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => {
                            handleAddInstruction(
                              workReq.workReqID,
                              index + "-" + workReq.workReqID
                            );
                          }}
                          disabled={!rules.canEdit}
                        >
                          <Icon icon="tabler:plus" />
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <FormLabel
                          sx={{
                            fontSize: "13px",
                            color: "text.primary",
                            fontWeight: 400,
                            mb: "4px",
                          }}
                        >
                          Property Area
                        </FormLabel>
                        <Box>
                      <CustomTextField
                  select
                  fullWidth
                  disabled={!rules.canEdit}
                  multiple
                  value={safeParseFirstAreaID(workRequirementList, workReq.workReqID)} // should return an array
                  onBlur={(e) =>
                    handleWorkRequirementChange(
                      workReq.workReqID,
                      "propertyAreaIDs",
                      e.target.value
                    )
                  }
                  onChange={(e) => {
                    let value = e.target.value;
                    const selected = safeParseFirstAreaID(workRequirementList, workReq.workReqID);
                    const allAreaIDs = [0, ...PropertyAreas.map((a) => a.propertyAreaID)];

                    if (value.includes("__create__")) {
                      handleOpenPropertyAreaDlg(workReq.workReqID);
                      value = value.filter((v) => v !== "__create__");
                      return;
                    }

                    if (value.includes("select_all")) {
                      const allSelected = allAreaIDs.every((id) => selected.includes(id));
                      value = allSelected ? [] : allAreaIDs; // toggle select/unselect all
                      value = value.filter((v) => v !== "select_all");
                      return handleChange(workReq.workReqID, "propertyAreaIDs", value);
                    }

                    handleChange(workReq.workReqID, "propertyAreaIDs", value);
                  }}
                  sx={{
                    backgroundColor: theme.palette.mode === "dark" ? "#1B2431" : "#fff",
                  }}
                  SelectProps={{
                    multiple: true,
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          maxHeight: 240, 
                        },
                      },
                    },
                    renderValue: (selected) => {
                      if (selected.length === 0) return "Select"; 
                      if (
                        selected.length === PropertyAreas.length + 1 &&
                        selected.includes(0)
                      )
                        return "Entire Property (All Areas Selected)";

                      const selectedLabels = [];

                      if (selected.includes(0)) selectedLabels.push("Entire Property");

                      PropertyAreas.forEach((area) => {
                        if (selected.includes(area.propertyAreaID))
                          selectedLabels.push(area.propertyAreaName);
                      });

                      return selectedLabels.join(", ");
                    },
                  }}
                >
                  {/* Create Property Area */}
                  <MenuItem key="__create__" value="__create__">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AddIcon fontSize="small" />
                      Create Property Area
                    </Box>
                  </MenuItem>

                  <ListSubheader>Select Options</ListSubheader>

                  {/* Select All */}
                  <MenuItem key="select_all" value="select_all">
                    <Checkbox
                      indeterminate={
                        (() => {
                          const selected = safeParseFirstAreaID(workRequirementList, workReq.workReqID);
                          const allIDs = [0, ...PropertyAreas.map((a) => a.propertyAreaID)];
                          const selectedCount = selected.filter((id) => allIDs.includes(id)).length;
                          return selectedCount > 0 && selectedCount < allIDs.length;
                        })()
                      }
                      checked={
                        (() => {
                          const selected = safeParseFirstAreaID(workRequirementList, workReq.workReqID);
                          const allIDs = [0, ...PropertyAreas.map((a) => a.propertyAreaID)];
                          return allIDs.every((id) => selected.includes(id));
                        })()
                      }
                    />
                    <ListItemText primary="Select All" />
                  </MenuItem>

                  <ListSubheader>Property Areas</ListSubheader>

                  {/* Entire Property + all Property Areas */}
                  {[
                    { propertyAreaID: 0, propertyAreaName: "Entire Property" },
                    ...PropertyAreas,
                  ].map((propArea) => (
                    <MenuItem key={propArea.propertyAreaID} value={propArea.propertyAreaID}>
                      <Checkbox
                        checked={safeParseFirstAreaID(workRequirementList, workReq.workReqID).includes(
                          propArea.propertyAreaID
                        )}
                      />
                      <ListItemText primary={propArea.propertyAreaName} />
                    </MenuItem>
                  ))}
                </CustomTextField>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        {(entity === "estimate" ||
                          entity === "estimate-detail" ||
                          entity === "workOrder" ||
                          entity === "workOrder-detail" ||
                          entity === "workOrder-schedule") && (
                          <>
                            <FormLabel
                              sx={{
                                fontSize: "13px",
                                color: "text.primary",
                                fontWeight: 400,
                                mb: "4px",
                              }}
                            >
                              Billing Type
                            </FormLabel>
                            <Box>
                              <CustomTextField
                                select
                                fullWidth
                                defaultValue="select"
                                value={
                                  workRequirementList.find(
                                    (w) => w.workReqID === workReq.workReqID
                                  )?.billingType || "select"
                                }
                                onChange={(e) =>
                                  handleWorkRequirementChange(
                                    workReq.workReqID,
                                    "billingType",
                                    e.target.value
                                  )
                                }
                                sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff" }}
                                disabled={!rules.canEdit}
                              >
                                <MenuItem value="select" disabled>
                                  Select Billing Type
                                </MenuItem>
                                <MenuItem value="Fixed">Flat Rate</MenuItem>
                                <MenuItem value="Time and Material">
                                  Time and Material
                                </MenuItem>
                              </CustomTextField>
                            </Box>
                            {(entity === "workOrder" ||
                              entity === "estimate" ||
                              entity === "estimate-detail" ||
                              entity === "workOrder-detail" ||
                              entity === "workOrder-schedule") && <></>}

                            {(entity === "estimate" ||
                              entity === "estimate-detail") && (
                              <>
                                <Grid item xs={12} md={12}>
                                  <FormLabel
                                    sx={{
                                      fontSize: "13px",
                                      color: "text.primary",
                                      fontWeight: 400,
                                      mb: "4px",
                                    }}
                                  >
                                    Job Type
                                  </FormLabel>
                                  <Box>
                                    <CustomTextField
                                      select
                                      fullWidth
                                      value={
                                        workRequirementList.find(
                                          (w) =>
                                            w.workReqID === workReq.workReqID
                                        )?.jobType || "One Time"
                                      }
                                      onChange={(e) =>
                                        handleWorkRequirementChange(
                                          workReq.workReqID,
                                          "jobType",
                                          e.target.value
                                        )
                                      }
                                      defaultValue="select"
                                      sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff" }}
                                      disabled={!rules.canEdit}
                                    >
                                      {/* <MenuItem value="select" disabled>
                          Select Job Type
                        </MenuItem> */}
                                      <MenuItem value="One Time">
                                        One Time
                                      </MenuItem>
                                      <MenuItem value="Recurring">
                                        Recurring
                                      </MenuItem>
                                    </CustomTextField>
                                  </Box>
                                </Grid>
                                <OccurrencesInput
                                  workReq={workReq}
                                  rules={rules}
                                  onChange={handleWorkRequirementChange}
                                />
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    </Grid>

                    {entity === "workOrder-detail" ||
                    entity === "workOrder-schedule" ? (
                      (() => {
                        const rawJobs =
                          scheduleMap[workReq.workReqID]?.jobs ??
                          workReq.jobs ??
                          [];
                        // console.log(rawJobs)
                        const validJobs = rawJobs.filter(
                          (j) => j.occurrenceNo > 0
                        );
                        if (validJobs.length) {
                          // console.log(validJobs)
                          return validJobs.map((job) => (
                            <Grid
                              item
                              xs={12}
                              key={job.jobID || job.occurrenceNo}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  p: 4,
                                  borderRadius: 2,
                                  border: "1px solid #E4E4E7",
                                  backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff"
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      cursor: "pointer",
                                      "&:hover": {
                                        color: "#000",
                                        textDecoration: "underline",
                                      },
                                    }}
                                    onClick={() => {
                                      handleOpenJobDetail(workReq, job);
                                    }}
                                  >
                                    Job {job.occurrenceNo}
                                  </Typography>
                                  <CustomChip
                                    skin="dark"
                                    color="main"
                                    label={(() => {
                                      // Format using new separate date/time fields
                                      if (!job.scheduleStartDate) {
                                        return "Unscheduled";
                                      }
                                      
                                      let dateStr = dayjs(job.scheduleStartDate).format("MM/DD/YY");
                                      if (job.scheduleStartTime) {
                                        const time = dayjs(`1970-01-01T${job.scheduleStartTime}`);
                                        dateStr += ` | ${time.format("h:mm A")}`;
                                      }
                                      return dateStr;
                                    })()}
                                    onClick={() => {
                                      handleOpenJobDetail(workReq, job);
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Grid>
                          ));
                        } else {
                          return (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No scheduled jobs yet
                              </Typography>
                            </Grid>
                          );
                        }
                      })()
                    ) : (
                      <Grid item xs={12}>
                        <ServiceLineItems
                          entity={entity}
                          rules={rules}
                          setLoading={setLoading}
                          toast={toast}
                          workReq={workReq}
                          setLineItemChanged={setLineItemChanged}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Collapse>
              </React.Fragment>
            );
          })
        ) : (
          <></>
        )}
      </List>

      {/* Popup Dialogs */}
      <JobDetailDialog
        open={openJobDetail}
        handleClose={handleCloseJobDetail}
        method={method}
        entity={entity}
        rules={rules}
        workReq={selectedWorkReq}
        job={selectedJob}
        setJob={setSelectedJob}
        fetchWorkRequirements={fetchWorkRequirements}
        setLoading={setLoading}
        toast={toast}
        fetchRequests={fetchRequests}
      />

      <JobScheduleDialog
        open={openSingleSchedule}
        handleClose={closeSingleSchedule}
        services={serviceToSchedule ? [serviceToSchedule] : []}
        fetchWorkRequirements={fetchWorkRequirements}
        requestID={requestID}
        toast={toast}
      />

      <AddPropertyAreaDialog
        open={openPropertyAreaDlg}
        handleClose={handleClosePropertyAreaDlg}
        jobsite={jobsite} // uses jobsite.jobsiteID we set in step 1
        navigate={navigate}
        propertyArea={propertyArea}
        setPropertyArea={setPropertyArea}
        areaMeasurement={areaMeasurement}
        setAreaMeasurement={setAreaMeasurement}
        clearAreaMeasurement={() =>
          setAreaMeasurement({
            measurementName: "",
            unitOfMeasure: "",
            length: "",
            width: "",
            height: "",
            total: "",
          })
        }
        toast={toast}
        setLoading={setLoading}
        fetchPropertyArea={() => {
          /* optional; we call fetchPropertyAreas instead */
        }}
        listOfMeasurements={listOfMeasurements}
        setListOfMeasurements={setListOfMeasurements}
        propertyContent={{ title: "Create Property Area", mode: "create" }}
        onSaved={handlePropertyAreaSaved}
      />
    </>
  );
};

export default Service;
